import type { PuppyId, Color } from './types';

// Mirrors Square.swift::SquareStruct (the pure-data part).
export interface SquareData {
  index: number;
  color: Color | null;
  puppy: PuppyId | null;
  isClear: boolean;
  isCrazy: boolean;
  isFireHydrant: boolean;
  underCrazyMega: boolean;
  scoreIfPlayable: number;
  hasBonus: boolean;
}

export function emptySquareData(index = 0): SquareData {
  return {
    index,
    color: null,
    puppy: null,
    isClear: false,
    isCrazy: false,
    isFireHydrant: false,
    underCrazyMega: false,
    scoreIfPlayable: 0,
    hasBonus: false,
  };
}

// Mirrors the parts of Square.swift that are pure logic.
// SpriteKit fields (sprites, position, CGRect) are deferred to the render layer (Plan 2).
export class Square {
  data: SquareData;
  readonly row: number;
  readonly col: number;
  readonly leftSqNdx: number;
  readonly topSqNdx: number;
  readonly rightSqNdx: number;
  readonly bottomSqNdx: number;

  constructor(data: SquareData, numSquaresHoriz: number, numSquares: number) {
    this.data = { ...data };
    this.row = Math.floor(data.index / numSquaresHoriz);
    this.col = data.index % numSquaresHoriz;
    this.leftSqNdx = this.col > 0 ? data.index - 1 : -1;
    this.topSqNdx = data.index - numSquaresHoriz >= 0 ? data.index - numSquaresHoriz : -1;
    this.rightSqNdx = this.col < numSquaresHoriz - 1 ? data.index + 1 : -1;
    this.bottomSqNdx = data.index + numSquaresHoriz < numSquares ? data.index + numSquaresHoriz : -1;
  }

  get isEmpty(): boolean {
    return this.data.puppy === null;
  }

  place(puppy: PuppyId, color: Color): void {
    this.data.puppy = puppy;
    this.data.color = color;
  }

  // Test-fixture helper — clears a cell to "empty dirt". No production callers
  // (the equivalent transitions in production go through Board.clearRowOrColumn
  // or Board.convertMegaToCrazy, which set additional fields).
  clearSquare(): void {
    this.data.isClear = true;
    this.data.puppy = null;
    this.data.color = null;
  }

  toJSON(): SquareData {
    return { ...this.data };
  }

  static fromJSON(data: SquareData, numSquaresHoriz: number, numSquares: number): Square {
    return new Square(data, numSquaresHoriz, numSquares);
  }
}
